import { useMemo, useCallback, useRef } from 'react';
import { useApi } from '../../provider';
import { useQuery, useMutation } from '../../hooks';
import { queryCache } from '../../cache';
import { ProductService } from './service';
import {
  Product,
  CreateProductRequest,
  UpdateProductRequest,
  PutProductResult,
  ForkProductRequest,
  ForkProductResponse,
  ForkAllProductRequest,
  ForkAllProductResponse,
  ProductsQueryParams,
  FlatProductQueryResponse,
  NamePrecedenceStep,
  ProductNameVariant,
  FdaSourceKey,
  FdaSourceSearchResponse,
} from './types';

const CACHE_KEY = 'products';
const FLAT_CACHE_KEY = 'products-flat';
const NAME_PRECEDENCE_CONFIG_CACHE_KEY = 'products-name-precedence-config';
const NAME_VARIANTS_CACHE_KEY = 'products-name-variants';
const FDA_SOURCE_SEARCH_CACHE_KEY = 'fda-source-search';

export function useProduct(id: number, options?: { enabled?: boolean }) {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);
  const fetcher = useCallback(() => service.getById(id), [service, id]);

  return useQuery<Product>(`${CACHE_KEY}/${id}`, fetcher, {
    enabled: options?.enabled ?? true,
  });
}

/**
 * The product modal's precedence-order tooltip/hint text (SynonymsRadioField)
 * and Names-tab source labels are both built from this. Rarely changes (an
 * admin config update + batch recompute), but note useQuery's cache-read
 * path is currently disabled (see hooks.ts execute()) — this refetches on
 * every mount today, not just once per session.
 */
export function useNamePrecedenceConfig() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);
  const fetcher = useCallback(
    () => service.getNamePrecedenceConfig(),
    [service]
  );

  return useQuery<NamePrecedenceStep[]>(
    NAME_PRECEDENCE_CONFIG_CACHE_KEY,
    fetcher
  );
}

/**
 * The product modal's Names tab — every active name variant for a product,
 * ranked via Name_Precedence_Config. Refetches whenever the modal opens for
 * a different product (cache key includes the id, mirrors useProduct).
 */
export function useProductNameVariants(productId: number | undefined) {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);
  const fetcher = useCallback(() => {
    if (productId === undefined) {
      return Promise.reject(
        new Error('useProductNameVariants called with no productId')
      );
    }
    return service.getNameVariants(productId);
  }, [service, productId]);

  return useQuery<ProductNameVariant[]>(
    `${NAME_VARIANTS_CACHE_KEY}/${productId}`,
    fetcher,
    { enabled: productId !== undefined }
  );
}

export function useCreateProduct() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<Product, CreateProductRequest>(
    data => service.create(data),
    {
      onSuccess: product => {
        queryCache.invalidate(CACHE_KEY);
        if (product.product_id) {
          queryCache.invalidate(
            `${CACHE_KEY}?product_id=${product.product_id}`
          );
        }
      },
    }
  );
}

export function useUpdateProduct() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<PutProductResult, UpdateProductRequest>(
    data => service.update(data),
    {
      onSuccess: data => {
        // Only invalidate when the product was actually saved.
        // conflict / multi_result responses (status field present) produce no DB write.
        if (!('status' in data)) {
          queryCache.invalidate(CACHE_KEY);
        }
      },
    }
  );
}

export function useDeleteProduct() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<void, number>(id => service.delete(id), {
    onSuccess: () => {
      queryCache.invalidatePrefix(CACHE_KEY);
    },
  });
}

export function useForkProduct() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<
    ForkProductResponse,
    { productId: number; data: ForkProductRequest }
  >(({ productId, data }) => service.fork(productId, data), {
    onSuccess: () => {
      queryCache.invalidatePrefix(CACHE_KEY);
    },
  });
}

export function useForkAllProducts() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<
    ForkAllProductResponse,
    { productId: number; data: ForkAllProductRequest }
  >(({ productId, data }) => service.forkAll(productId, data), {
    onSuccess: () => {
      queryCache.invalidatePrefix(CACHE_KEY);
    },
  });
}

export function useDeleteAllProducts() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<void, void>(() => service.deleteAll(), {
    onSuccess: () => {
      queryCache.invalidatePrefix(CACHE_KEY);
    },
  });
}

// ===== FLAT PRODUCTS HOOKS =====
// Used for MUI DataGrid row grouping (denormalized Cartesian product)

/**
 * Hook for fetching flat denormalized product data
 * Returns Cartesian product rows for MUI DataGrid row grouping
 */
export function useProductsFlat(params?: ProductsQueryParams) {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  const cacheKey = useMemo(() => {
    if (!params) return FLAT_CACHE_KEY;

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (typeof value === 'object') {
        searchParams.append(key, JSON.stringify(value));
      } else {
        searchParams.append(key, String(value));
      }
    });

    return `${FLAT_CACHE_KEY}?${searchParams.toString()}`;
  }, [params]);

  const fetcher = useCallback(
    (signal?: AbortSignal) =>
      service.getAllFlat(
        params,
        signal
          ? {
              signal,
            }
          : undefined
      ),
    [service, params]
  );

  // Only enable the query when params is provided
  return useQuery<FlatProductQueryResponse>(cacheKey, fetcher, {
    enabled: params !== undefined,
  });
}

/**
 * Convenience hook for flat products operations
 * Use this for MUI DataGrid row grouping feature
 *
 * Returns denormalized Cartesian product rows where each row represents:
 * Product × Manufacturer × Facility × Grade × Therapeutic Area
 *
 * Note:
 * - total: Total Cartesian product rows (combinations)
 * - totalProducts: Total distinct products (for pagination)
 */
export function useProductsFlatPage(params?: ProductsQueryParams) {
  const productResponse = useProductsFlat(params);
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();
  const deleteAllProducts = useDeleteAllProducts();

  // Preserve previous totalProducts to prevent rowCount from dropping to 0 during loading
  // This prevents MUI DataGrid from resetting pagination when transitioning between pages
  const prevTotalProductsRef = useRef<number>(0);
  if (
    productResponse.status === 'success' &&
    productResponse.data?.totalProducts !== undefined
  ) {
    prevTotalProductsRef.current = productResponse.data.totalProducts;
  }

  return useMemo(
    () => ({
      // Query operations
      products: productResponse,

      // Mutation operations
      create: createProduct,
      update: updateProduct,
      delete: deleteProduct,
      deleteAll: deleteAllProducts,

      // Convenience getters for common states
      data:
        productResponse.status === 'success'
          ? (productResponse.data?.products ?? [])
          : [],
      total:
        productResponse.status === 'success'
          ? (productResponse.data?.total ?? 0)
          : 0,
      // Keep previous totalProducts value during loading to prevent pagination reset
      totalProducts:
        productResponse.status === 'success'
          ? (productResponse.data?.totalProducts ?? 0)
          : prevTotalProductsRef.current,
      isLoading: productResponse.status === 'loading',
      isError: productResponse.status === 'error',
      error: productResponse.status === 'error' ? productResponse.error : null,

      // Actions
      refetch: productResponse.refetch,
      invalidate: productResponse.invalidate,
    }),
    [
      productResponse,
      createProduct,
      updateProduct,
      deleteProduct,
      deleteAllProducts,
    ]
  );
}

export interface FdaSourceSearchParams {
  search: string;
  source: FdaSourceKey;
  page: number;
  pageSize: number;
  /** 'unlinked'/'linked' (docs/features/fda-source-record-browser.md, Task
   * FSU-004; 'linked' added for the "Sourceable via Navigator" deep link,
   * human direction, 2026-09-28) both bypass the empty-search-term gate
   * below -- an empty term under either mode means "the whole
   * linked/unlinked cohort," not "search not yet started." Omitted/'all'
   * preserves the exact pre-FSU-004 behavior. */
  linkFilter?: 'all' | 'unlinked' | 'linked' | 'cohort';
  /** Metrics Overview ingredient cohort key; only meaningful (and required)
   * with linkFilter='cohort' and source === 'ob' (docs/features/
   * metrics-overview-tab.md, Requirement 5). An empty term is a real query
   * under 'cohort' too. */
  cohort?: string;
  /** Only meaningful when source === 'ob'; ignored (and safely omittable)
   * otherwise. Wire-shaped to match api/routes/fdaSourceSearch.js's own
   * `obType` query param exactly. */
  obType?: string[];
  /** Only meaningful when source === 'ndc'. */
  ndcFinished?: string;
  /** Only meaningful when source === 'dmf'. */
  dmfStatus?: string;
}

/**
 * FDA Source Product Search (docs/features/fda-source-product-search.md,
 * Task FSS-008). Always called for one specific `source` — never `'all'` —
 * so each of FdaSourceSearchResults.tsx's three columns gets its own
 * independent hook instance, independent loading state, and independent
 * pagination (Decision 12); pass `params: undefined` for a source that
 * isn't currently visible (single-source filter active) to skip fetching
 * it entirely. `enabled` also requires a non-empty search term — the
 * debounce/min-length gate already happened upstream in BasicSearch.tsx
 * (Requirement 1), so this hook only needs to guard against an empty term
 * (e.g. right after the user clears the box).
 */
export function useFdaSourceSearch(params: FdaSourceSearchParams | undefined) {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  const cacheKey = useMemo(() => {
    if (!params) return FDA_SOURCE_SEARCH_CACHE_KEY;
    const searchParams = new URLSearchParams({
      search: params.search,
      source: params.source,
      page: String(params.page),
      pageSize: String(params.pageSize),
      linkFilter: params.linkFilter ?? 'all',
    });
    if (params.obType && params.obType.length > 0) {
      searchParams.set('obType', params.obType.join(','));
    }
    if (params.ndcFinished) {
      searchParams.set('ndcFinished', params.ndcFinished);
    }
    if (params.dmfStatus) {
      searchParams.set('dmfStatus', params.dmfStatus);
    }
    if (params.cohort) {
      searchParams.set('cohort', params.cohort);
    }
    return `${FDA_SOURCE_SEARCH_CACHE_KEY}?${searchParams.toString()}`;
  }, [params]);

  const fetcher = useCallback(
    (signal?: AbortSignal) => {
      if (!params) {
        return Promise.reject(new Error('useFdaSourceSearch: params required'));
      }
      return service.searchFdaSources(
        params.search,
        params.source,
        params.page,
        params.pageSize,
        signal,
        params.linkFilter,
        {
          obType: params.obType,
          ndcFinished: params.ndcFinished,
          dmfStatus: params.dmfStatus,
          cohort: params.cohort,
        }
      );
    },
    [service, params]
  );

  return useQuery<FdaSourceSearchResponse>(cacheKey, fetcher, {
    enabled:
      params !== undefined &&
      (params.search.trim().length > 0 ||
        params.linkFilter === 'unlinked' ||
        params.linkFilter === 'linked' ||
        params.linkFilter === 'cohort'),
  });
}
